/**
 * AI Engine Error Messages
 * 
 * Centralized user-friendly error messages for AI Engine failures.
 * Following the same pattern as Supabase error handler.
 */

/**
 * User-friendly error messages for different error scenarios
 */
export const AI_ERROR_MESSAGES = {
  // Service Unavailable (503, ECONNREFUSED, timeout)
  SERVICE_UNAVAILABLE: 'AI service is currently unavailable. Please try again in a few moments.',
  
  // Server Errors (500, 502, 504)
  SERVER_ERROR: 'Something went wrong while processing your request. Please try again later.',
  
  // Client Errors (400, 422)
  CLIENT_ERROR: 'Unable to process your request. Please check your input and try again.',
  
  // Rate Limiting (429)
  RATE_LIMIT: 'Too many requests. Please wait a moment before trying again.',
  
  // Authentication Errors (401): the sign-in is missing or has expired
  AUTH_ERROR: 'Authentication failed. Please sign in again.',

  // Permission Errors (403): signed in, but not allowed to do this
  FORBIDDEN: "You don't have access to this. Please contact your administrator.",
  
  // Network Errors (CORS, connection issues, backend unavailable)
  NETWORK_ERROR: 'AI Engine is currently unavailable. Please try again in a few moments.',
  
  // Timeout
  TIMEOUT: 'Request timed out. The AI service is taking longer than expected. Please try again.',
  
  // Generic fallback
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
} as const;

/**
 * Map HTTP status codes to user-friendly messages
 */
export function getErrorMessageForStatus(status: number | undefined): string {
  if (!status) {
    return AI_ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  // Service Unavailable
  if (status === 503) {
    return AI_ERROR_MESSAGES.SERVICE_UNAVAILABLE;
  }

  // Server Errors
  if (status === 500 || status === 502 || status === 504) {
    return AI_ERROR_MESSAGES.SERVER_ERROR;
  }

  // 404 on API endpoints likely means service is paused/down
  if (status === 404) {
    return AI_ERROR_MESSAGES.SERVICE_UNAVAILABLE;
  }

  // Client Errors
  if (status === 400 || status === 422) {
    return AI_ERROR_MESSAGES.CLIENT_ERROR;
  }

  // Rate Limiting
  if (status === 429) {
    return AI_ERROR_MESSAGES.RATE_LIMIT;
  }

  // Authentication vs permission: "sign in again" is wrong advice for a refused permission
  if (status === 401) {
    return AI_ERROR_MESSAGES.AUTH_ERROR;
  }
  if (status === 403) {
    return AI_ERROR_MESSAGES.FORBIDDEN;
  }

  // Default for other status codes
  if (status >= 500) {
    return AI_ERROR_MESSAGES.SERVER_ERROR;
  }

  if (status >= 400) {
    return AI_ERROR_MESSAGES.CLIENT_ERROR;
  }

  return AI_ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Map error codes to user-friendly messages
 */
export function getErrorMessageForCode(code: string | undefined): string {
  if (!code) {
    return AI_ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  switch (code) {
    case 'ECONNREFUSED':
    case 'ENOTFOUND':
    case 'ENETUNREACH':
    case 'ERR_NETWORK':
    case 'ERR_BAD_REQUEST':  // Often triggered by CORS
    case 'ERR_CONNECTION_REFUSED':
      return AI_ERROR_MESSAGES.SERVICE_UNAVAILABLE;

    case 'ECONNABORTED':
    case 'ETIMEDOUT':
    case 'ERR_TIMEOUT':
      return AI_ERROR_MESSAGES.TIMEOUT;

    default:
      return AI_ERROR_MESSAGES.NETWORK_ERROR;
  }
}

